import React, { useEffect, useState } from "react";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../common/FormRenderer";
import { ACTIONS } from "../../common/Constants";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { getValue } from "../../../utils/common";
import { useGetCompanies, useGetCompanyLocations, useGetDepartmentUserMappings } from "../../../backend/masters";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../common/Table";
import { ACTIVITY_TYPE, API_DELIMITER } from "../../../utils/constants";
import { getUserDetails, isAdmin, isComplianceUser } from "../../../backend/auth";
import { useGetUserCompanies } from "../../../backend/query";
import { sortBy } from "underscore";

const INCLUDE_USER_COMPANY_ASSOCIATIONS = {
  parent_company: {
    associate_companies: {
      company_location_mappings: {
        location: ['city']
      }
    }
  }
};

function locationOptionLabel({ label, location }: any) {
    return (
        <div className="d-flex flex-column">
            <div>{label}</div>
            {
                Boolean(location) &&
                <>
                    <div className="text-sm fw-bold">{location.city.label} | {location.state.label}</div>
                </>
            }
        </div>
    )
}

export default function CompanyDetails(this: any, { action, data, onSubmit }: any) {

    const [companiesList, setCompanies] = useState<any[]>([]);
    const [associateCompaniesList, setAssociateCompanies] = useState<any[]>([]);
    const [locationsList, setLocations] = useState<any[]>([]);
    const [details, setDetails] = useState({});
    const [parentCompany, setParentCompany] = useState<any>();
    const [associate_company, setAssociateCompany] = useState<any>();
    const [establishment_types, setEstablishmentTypes] = useState<any[]>([]);

    const user:any = getUserDetails();

    const { companies: parentCompanies, isFetching: fetchingParentCompanies } = useGetCompanies({
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { is_parent_eq: true, is_active_eq: true }
    }, isAdmin());

    const { companies: associateCompanies, isFetching: fetchingAssociateCompanies } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {
          is_parent_eq: false,
          parent_company_id_eq: (parentCompany || {}).id,
          is_active_eq: true
        }
    }, Boolean(parentCompany) && (isAdmin() || isComplianceUser()));

    const { locations, isFetching: fetchingLocs } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: { 
          company_id_eq: (associate_company || {}).id 
        },
        sorts: ['location_name asc'],
        includes: { location: ['city'] },
        include: { 
          location: { 
            include: { 
              city: { 
                include: {
                  state: { 
                    only: ['id','name'] 
                  }
                }
              }
            } 
          } 
        }
    }, Boolean(associate_company) && (isAdmin() || isComplianceUser()));

    const { userCompanies, isFetching: fetchingUserCompanies }: any = useGetUserCompanies({
      filters: {
        users_id_eq: user.user_id,
        is_active_eq: true
      },
      includes: INCLUDE_USER_COMPANY_ASSOCIATIONS,
    }, !isAdmin() && !isComplianceUser());

    const { departmentUsers, isFetching: fetchingDepartmentUsers }: any = useGetDepartmentUserMappings({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: { user_id_eq: getUserDetails().user_id, is_active_eq: true }
    }, isComplianceUser());

    const schema = {
        fields: [
            {
                component: action === ACTIONS.ADD ? componentTypes.SELECT : componentTypes.PLAIN_TEXT,
                name: 'company',
                label: 'Company',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                isLoading: fetchingParentCompanies,
                content: action !== ACTIONS.ADD ? getValue(details, 'company.label') : '',
                options: companiesList,
                onChange: handleCompanyChange.bind(this)
            },
            {
                component: action === ACTIONS.ADD ? componentTypes.SELECT : componentTypes.PLAIN_TEXT,
                name: 'associate_company',
                label: 'Associate Company',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                content: action !== ACTIONS.ADD ? getValue(details, 'associate_company.label') : '',
                options: associateCompaniesList,
                isDisabled: !Boolean(parentCompany),
                isLoading: fetchingAssociateCompanies,
                onChange: handleAssociateCompanyChange.bind(this)
            },
            {
                component: action === ACTIONS.ADD ? componentTypes.SELECT : componentTypes.PLAIN_TEXT,
                name: 'location',
                label: 'Location',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                options: (locationsList || []).map(({ location }: any) => {
                    const { id, name, code, city } = location;
                    const { state } = city;
                    return {
                        id, name, code,
                        city: { code: city.code, label: city.name, value: city.id },
                        state: { code: state.code, label: state.name, value: state.id }
                    }
                }),
                content: action !== ACTIONS.ADD ? getValue(details, 'location.label') : '',
                isDisabled: !Boolean(associate_company),
                isLoading: fetchingLocs,
                onChange: (location: any) => {
                    setDetails({ ...details, location, state_id: getValue(location, 'location.state.value') });
                },
                formatOptionLabel: action === ACTIONS.ADD ? locationOptionLabel : '',
            },
            {
                component: action === ACTIONS.ADD ? componentTypes.SELECT : componentTypes.PLAIN_TEXT,
                name: 'establishment_type',
                label: 'Establishment Type',
                options: establishment_types,
                content: action !== ACTIONS.ADD ? getValue(details, 'establishment_type.label') : '',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                onChange: ({ value, label }: any) => {
                    setDetails({ ...details, establishment_type: { value, label } });
                }
            },
            {
                component: action === ACTIONS.ADD ? componentTypes.SELECT : componentTypes.PLAIN_TEXT,
                name: 'auditted',
                label: 'Audit Type',
                options: Object.values(ACTIVITY_TYPE),
                content: action !== ACTIONS.ADD ? getValue(details, 'auditted.label') : '',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                onChange: (auditted: any) => {
                    setDetails({ ...details, auditted });
                }
            }
        ]
    };

    function handleSubmit() {
        onSubmit(details)
    }

    function handleCompanyChange({ company }: any) {
        setParentCompany(company);
        setAssociateCompany(undefined);
        setAssociateCompanies([]);
        setLocations([]);
        setEstablishmentTypes([]);
        setDetails({
            ...details,
            company: { value: company.id, label: company.name, company },
            associate_company: null,
            location: null,
            state_id: null,
            establishment_type: null
        });
        if (!isAdmin() && !isComplianceUser()) {
            const sorted: any[] = sortBy(company.associateCompanies, 'name');
            setAssociateCompanies(sorted);
        }
    }

    function handleAssociateCompanyChange({ associate_company }: any) {
        setAssociateCompany(associate_company);
        setLocations([]);
        setEstablishmentTypes(associate_company.establishment_type.split(API_DELIMITER).filter((x: string) => !!x));
        setDetails({
            ...details,
            associate_company: { value: associate_company.id, label: associate_company.name, associate_company },
            location: null,
            state_id: null,
            establishment_type: null
        });
        if (!isAdmin() && !isComplianceUser()) {
            const locations = (associate_company.locations || []).map((location: any) => {
                return { label: `${location.name}, ${location.city.name}`, value: location.id, location, state_id: location.state_id };
            });
            const sorted = sortBy(locations, 'label');
            setLocations(sorted);
        }
    }

    useEffect(() => {
        if (Object.keys(data || {}).length) {
            const { company, associate_company } = data;
            setParentCompany(company.company);
            setAssociateCompany(associate_company.associate_company);
            setEstablishmentTypes(associate_company.associate_company.establishment_type.split(API_DELIMITER));
            setDetails({ ...details, ...data });
        }
    }, [data]);

    useEffect(() => {
        if (!fetchingUserCompanies && userCompanies) {
            const sorted = sortBy(userCompanies.map((company: any) => {
                const {associate_companies} = company;
                return {
                    ...company,
                    associateCompanies: associate_companies.map((associate_company: any) => {
                        return {
                            ...associate_company,
                            locations: associate_company.locations
                        }
                    })
                }

            }), 'name');
            setCompanies(sorted);
        }
    }, [fetchingUserCompanies]);

    useEffect(() => {
        if (!fetchingDepartmentUsers && departmentUsers && isComplianceUser()) {
            const _companies: any = {};
            console.log(departmentUsers)
            departmentUsers.forEach(({ department, userId, user }: any) => {
                const { vertical } = department;
                const { company } = vertical;
                const { id, name } = company;
                if (!_companies[id]) {
                    _companies[id] = company;
                }
            });
            setCompanies(sortBy(Object.values(_companies), 'name'))
        }

    }, [fetchingDepartmentUsers]);

    useEffect(() => {
        if (!fetchingParentCompanies && parentCompanies && isAdmin()) {
            setCompanies(parentCompanies);
        }
    }, [fetchingParentCompanies]);

    useEffect(() => {
        if (!fetchingAssociateCompanies && associateCompanies && (isAdmin() || isComplianceUser())) {
            setAssociateCompanies(associateCompanies);
        }
    }, [fetchingAssociateCompanies]);

    useEffect(() => {
        if (!fetchingLocs && locations && (isAdmin() || isComplianceUser())) {
            const locs = locations.map(({ location }: any) => {
                const { id, name, city } = location;
                const {state} = city;
                return { label: `${name}, ${city.name}`, value: id, location, state_id: state.id };
            });
            const sorted = sortBy(locs, 'label');
            setLocations(sorted);
        }
    }, [fetchingLocs]);

    return (
        <>
            <FormRenderer FormTemplate={FormTemplate}
                initialValues={{
                    buttonWrapStyles: 'justify-content-start',
                    submitBtnText: 'Next',
                    ...details,
                    hideButtons: action === ACTIONS.VIEW
                }}
                componentMapper={ComponentMapper}
                schema={schema}
                onSubmit={handleSubmit}
            />
        </>
    )
}
